import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { LuRefreshCw, LuTrash2, LuWandSparkles } from 'react-icons/lu';
import { Modal } from '../ui/Modal';
import Button from '../ui/Button';
import Alert from '../ui/Alert';
import ConfirmModal from '../ui/ConfirmModal';
import Field, { INPUT_CLASS } from '../ui/Field';

/**
 * Dialogs of the custom analyses (create, delete, regenerate with an edited prompt).
 * Opened imperatively by AnalysesSection: ref.openCreate(), openDelete(a), openRegenerate(a).
 */
export const ModalAnalysis = forwardRef(({ onCreateAnalysis, onDeleteAnalysis, onRegenerateCustomAnalysis, extractOriginalPrompt }, ref) => {
  const [mode, setMode] = useState(null);
  const [target, setTarget] = useState(null);
  const [title, setTitle] = useState('');
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const titleRef = useRef(null);
  const promptRef = useRef(null);

  const open = (nextMode, analysis = null) => {
    setMode(nextMode);
    setTarget(analysis);
    setError(null);
    setTitle('');
    setPrompt(nextMode === 'regenerate' && extractOriginalPrompt ? extractOriginalPrompt(analysis) : '');
  };

  const close = () => {
    if (busy) return;
    setMode(null);
    setTarget(null);
  };

  useImperativeHandle(ref, () => ({
    openCreate: () => open('create'),
    openDelete: (analysis) => open('delete', analysis),
    openRegenerate: (analysis) => open('regenerate', analysis),
  }));

  const run = async (action, failure) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      setBusy(false);
      setMode(null);
      setTarget(null);
    } catch (err) {
      console.error(failure, err);
      setError(err?.message || failure);
      setBusy(false);
    }
  };

  const submitCreate = (e) => {
    e.preventDefault();
    if (!title.trim() || !prompt.trim()) {
      setError('Renseignez un titre et un prompt.');
      return;
    }
    run(() => onCreateAnalysis({ title: title.trim(), prompt: prompt.trim() }), 'Erreur lors de la création de l\'analyse');
  };

  const submitRegenerate = (e) => {
    e.preventDefault();
    if (!prompt.trim()) {
      setError('Saisissez un prompt pour régénérer l\'analyse.');
      return;
    }
    run(() => onRegenerateCustomAnalysis(target.type, prompt), 'Erreur lors de la régénération de l\'analyse');
  };

  const promptField = (id, label, hint) => (
    <Field label={label} htmlFor={id} hint={hint}>
      <textarea
        id={id}
        ref={promptRef}
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={7}
        disabled={busy}
        placeholder="Décrivez ce que l'IA doit analyser dans les messages de la semaine…"
        className={`${INPUT_CLASS} resize-y leading-relaxed`}
      />
    </Field>
  );

  return (
    <>
      <Modal
        isOpen={mode === 'create'}
        onClose={close}
        dismissible={!busy}
        icon={LuWandSparkles}
        title="Nouvelle analyse personnalisée"
        subtitle="L'IA relit les messages de la semaine selon vos consignes."
        size="md"
        initialFocusRef={titleRef}
        footer={(
          <>
            <Button onClick={close} disabled={busy}>Annuler</Button>
            <Button type="submit" form="create-analysis-form" variant="primary" icon={LuWandSparkles} loading={busy}>
              {busy ? 'Génération…' : 'Générer l\'analyse'}
            </Button>
          </>
        )}
      >
        <form id="create-analysis-form" onSubmit={submitCreate} className="space-y-4">
          {error && <Alert tone="danger">{error}</Alert>}
          <Field label="Titre de l'analyse" htmlFor="analysis-title">
            <input
              id="analysis-title"
              ref={titleRef}
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={busy}
              placeholder="Ex. : Focus lancement produit"
              className={INPUT_CLASS}
            />
          </Field>
          {promptField('analysis-prompt', 'Prompt', 'La génération prend en général moins d\'une minute.')}
        </form>
      </Modal>

      <Modal
        isOpen={mode === 'regenerate'}
        onClose={close}
        dismissible={!busy}
        icon={LuRefreshCw}
        title="Régénérer l'analyse"
        subtitle={target?.title}
        size="md"
        initialFocusRef={promptRef}
        footer={(
          <>
            <Button onClick={close} disabled={busy}>Annuler</Button>
            <Button type="submit" form="regenerate-analysis-form" variant="primary" icon={LuRefreshCw} loading={busy} disabled={!prompt.trim()}>
              {busy ? 'Régénération…' : 'Régénérer'}
            </Button>
          </>
        )}
      >
        <form id="regenerate-analysis-form" onSubmit={submitRegenerate} className="space-y-4">
          {error && <Alert tone="danger">{error}</Alert>}
          <Alert tone="warning">Le contenu actuel, modifications manuelles comprises, sera remplacé.</Alert>
          {promptField('regenerate-prompt', 'Prompt (modifiable)', null)}
        </form>
      </Modal>

      <ConfirmModal
        isOpen={mode === 'delete'}
        onClose={close}
        onConfirm={() => run(() => onDeleteAnalysis(target.id), 'Erreur lors de la suppression de l\'analyse')}
        loading={busy}
        icon={LuTrash2}
        title="Supprimer l'analyse ?"
        message={(
          <>
            L'analyse <strong className="text-gray-900 dark:text-white">« {target?.title || target?.type} »</strong> sera définitivement supprimée.
            {error && <span className="mt-2 block text-red-600 dark:text-red-400">{error}</span>}
          </>
        )}
        confirmLabel="Supprimer"
      />
    </>
  );
});

ModalAnalysis.displayName = 'ModalAnalysis';

export default ModalAnalysis;
